import { useAct } from 'FlowBuilder/store'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import './DonateActionForm.scoped.scss'
import ThankYouEmailSettingsForm from './Components/ThankYouEmailSettingsForm'

export default function DonationAmountsForm({ actionId }) {
  const act = useAct()
  const [newAmount, setNewAmount] = useState('')

  const action = useSel(s => s.actions.entities[s.local.selectedActionId])
  const config = action.config || { amounts: [] }
  const amounts = config.amounts || []

  function updateAmounts(data) {
    act.actions.upsert(actionId, { config: { ...config, amounts: data } })
  }

  function updateConfig(newConfig) {
    act.actions.upsert(actionId, { config: { ...config, ...newConfig } })
  }

  function handleAddAmount(e) {
    if (newAmount === '£') return

    const poundsValue = parseFloat(newAmount.replace('£', ''))
    if (isNaN(poundsValue) || poundsValue <= 0) return

    const penniesValue = Math.round(poundsValue * 100)
    updateAmounts([...amounts, penniesValue])
    setNewAmount('£')
  }

  function handleInputChange(e) {
    const value = e.target.value

    if (value === '') {
      setNewAmount('£')
      return;
    }

    const numericPart = value.replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1')

    setNewAmount(`£${numericPart}`)
  }

  useEffect(() => {
    if (amounts.length === 0) {
      setNewAmount('£')
    }
  }, [])

  function handleKeyDownWithEnter(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddAmount(e)
    }

    if (e.key === 'Backspace' && (newAmount === '£' || newAmount.length === 1)) {
      e.preventDefault()
    }
  }

  function handleRemoveAmount(index) {
    const newAmounts = [...amounts]
    newAmounts.splice(index, 1)
    updateAmounts(newAmounts)
  }

  function onDragEnd(result) {
    if (!result.destination) return

    const items = Array.from(amounts)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    updateAmounts(items)
  }

  function formatAmount(pennies) {
    return (pennies / 100).toFixed(2)
  }

  return (
    <div className="box narrow margin-bottom double">
      <h2 className='no-margin-top'>Donation amounts</h2>

      <div className='field margin-top'>
        <label className="bold">Donation prompts (drag to reorder - left appears first)</label>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="amounts" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-wrap margin-top"
              >
                {amounts.map((amount, index) => (
                  <Draggable key={`${amount}-${index}`} draggableId={`${amount}-${index}`} index={index}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="amount-tag">
                        £{formatAmount(amount)}
                        <button onClick={() => handleRemoveAmount(index)} className="close-button">×</button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {amounts.length === 0 && (
          <div className="margin-top text-muted">No donation amounts added yet.</div>
        )}
      </div>

      <div className='field'>
        <label className="dark-grey bold margin-top">Add new donation prompt</label>
        <div className="flex margin-top">
          <div className="flex-grow">
            <input
              type="text"
              value={newAmount}
              onChange={handleInputChange}
              onKeyDown={handleKeyDownWithEnter}
            />
          </div>
          <button
            onClick={handleAddAmount}
            className="button primary margin-left"
            disabled={!newAmount}
          >
            Add
          </button>
        </div>
      </div>

      <div className='field margin-top'>
        <label className="bold">Pre-selected donation prompt</label>
        {amounts.length > 0 ? (
          <select
            className="margin-top"
            value={config.defaultAmount || ''}
            onChange={e => updateConfig({ defaultAmount: e.target.value })}
          >
            <option value="" disabled>Select a default amount</option>
            {amounts.map((amount, index) => (
              <option key={index} value={amount}>£{formatAmount(amount)}</option>
            ))}
          </select>
        ) : (
          <div className="margin-top text-muted">Add donation amounts first</div>
        )}
      </div>

      <h2 className='margin-top double'>Donation form settings</h2>

      <div className='flex field'>
        <label className="bold">Enable monthly donation option</label>
        <input
          className="margin-left"
          type="checkbox"
          checked={config.enableMonthlyDonations}
          onChange={e => updateConfig({ enableMonthlyDonations: e.target.checked })}
        />
      </div>

      {config.enableMonthlyDonations && 
        (<div className='flex field margin-top'>
          <label className="bold">Enable monthly upsell screen</label>
          <input
            className="margin-left"
            type="checkbox"
            checked={config.enableMonthlyUpsell}
            onChange={e => updateConfig({ enableMonthlyUpsell: e.target.checked })}
          />
        </div>
      )}

      <div className='flex field margin-top'>
        <label className="bold">Enable cover fee checkbox</label>
        <input
          className="margin-left"
          type="checkbox"
          checked={config.coverFee}
          onChange={e => updateConfig({ coverFee: e.target.checked, preTickCoverFee: false })}
        />
      </div>

      {config.coverFee && (
        <div className='flex field margin-top'>
          <label className="bold">Pre-tick cover fee checkbox</label>
          <input
            className="margin-left"
            type="checkbox"
            checked={config.preTickCoverFee}
            onChange={e => updateConfig({ preTickCoverFee: e.target.checked })}
          />
        </div>
      )}

      <h2 className='margin-top double'>Customise language</h2>

      {config.enableMonthlyUpsell && (
        <>
          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell text</label>
            <textarea
              className="margin-top half full-width"
              value={config.monthlyUpsellText || ''}
              onChange={e => updateConfig({ monthlyUpsellText: e.target.value })}
            />
          </div>

          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell button text</label>
            <input
              className="margin-top half"
              type="text"
              value={config.monthlyUpsellButton || ''}
              onChange={e => updateConfig({ monthlyUpsellButton: e.target.value })}
            />
          </div>

          <div className='field margin-top'>
            <label className="bold dark-grey">Monthly upsell one-off button text</label>
            <input
              className="margin-top half"
              type="text"
              value={config.oneOffButton || ''}
              onChange={e => updateConfig({ oneOffButton: e.target.value })}
            />
          </div>
        </>
      )}

      {config.coverFee && (
        <div className='field margin-top'>
          <label className="bold dark-grey">Label for cover fee prompt</label>
          <textarea
            className="margin-top half full-width"
            value={config.coverFeeText || ''}
            onChange={e => updateConfig({ coverFeeText: e.target.value })}
          />
        </div>
      )}
      
      <div className= "margin-top double">
        <ThankYouEmailSettingsForm actionId={actionId} additionalMergeTags={{
          'This Donation': [{
            'featured': true,
            'slug': 'amount',
            'label': 'This Donation Amount',
            'description': 'The amount of this donation'
            }, {
              'featured': true,
              'slug': 'is_regular_donation',
              'label': 'Is a Regular Donation',
              'description': 'Whether the donation is a monthly subscription or a one-off'
            }]
          }} />
      </div>
    </div>
  )
}
